import type { License } from "../../../../../../store/reseller-admin/licensesStore";
import type { ThemeClasses } from "../types";
import {
  BILLING_MODELS,
  BILLING_CYCLES,
  CURRENCIES,
  STATUS_OPTIONS,
  ENTITY_LIMIT_KEYS,
  AVAILABLE_APPS,
} from "../types";

interface LicenseDetailsModalProps {
  license: License;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: (license: License) => void;
  onDelete: (license: License) => void;
}

export default function LicenseDetailsModal({
  license,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
  onDelete,
}: LicenseDetailsModalProps) {
  const statusOpt = STATUS_OPTIONS.find((s) => s.value === license.status);
  const billingModel = BILLING_MODELS.find((b) => b.value === license.billing_model);
  const billingCycle = BILLING_CYCLES.find((c) => c.value === license.billing_cycle);
  const currency = CURRENCIES.find((c) => c.value === license.currency);

  const statusColors: Record<string, string> = {
    active: isDarkMode ? "bg-green-900/40 text-green-400 border-green-700" : "bg-green-50 text-green-700 border-green-200",
    inactive: isDarkMode ? "bg-red-900/40 text-red-400 border-red-700" : "bg-red-50 text-red-700 border-red-200",
    draft: isDarkMode ? "bg-yellow-900/40 text-yellow-400 border-yellow-700" : "bg-yellow-50 text-yellow-700 border-yellow-200",
  };

  const getPrice = () => {
    if (license.billing_model === "per_unit" && license.price_per_unit != null) {
      return `$${license.price_per_unit.toFixed(2)} ${license.currency}/unidad`;
    }
    if (license.billing_model === "flat" && license.flat_price != null) {
      return `$${license.flat_price.toFixed(2)} ${license.currency}`;
    }
    return "Por definir";
  };

  const entityLimits = license.entity_limits || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl ${themeClasses.bg} shadow-2xl`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-primary-500/20 text-primary-400" : "bg-primary-50 text-primary-600"}`}>
              <i className="ri-key-2-line text-xl"></i>
            </div>
            <div>
              <h2 className={`text-lg font-bold ${themeClasses.text}`}>{license.name}</h2>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${statusColors[license.status] || statusColors.draft}`}>
                {statusOpt?.label || license.status}
              </span>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={onClose} className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}>
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          {license.description && (
            <div>
              <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-1`}>Descripción</h3>
              <p className={`text-sm ${themeClasses.text}`}>{license.description}</p>
            </div>
          )}

          {/* Pricing */}
          <div className={`p-4 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3`}>
              <i className="ri-money-dollar-circle-line mr-1"></i> Facturación
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>Precio</p>
                <p className={`font-bold text-lg ${themeClasses.text}`}>{getPrice()}</p>
              </div>
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>Modelo</p>
                <p className={`font-medium text-sm ${themeClasses.text}`}>{billingModel?.label}</p>
              </div>
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>Ciclo</p>
                <p className={`font-medium text-sm ${themeClasses.text}`}>{billingCycle?.label}</p>
              </div>
              <div>
                <p className={`text-xs ${themeClasses.textSecondary}`}>Moneda</p>
                <p className={`font-medium text-sm ${themeClasses.text}`}>{currency?.label || license.currency}</p>
              </div>
            </div>
          </div>

          {/* Apps */}
          {license.apps && license.apps.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-2`}>
                <i className="ri-apps-line mr-1"></i> Apps incluidas
              </h3>
              <div className="flex flex-wrap gap-2">
                {license.apps.map((appId) => {
                  const app = AVAILABLE_APPS.find((a) => a.id === appId);
                  return (
                    <span
                      key={appId}
                      className={`text-sm px-3 py-1.5 rounded-lg ${isDarkMode ? "bg-blue-900/30 text-blue-400 border border-blue-800" : "bg-blue-50 text-blue-700 border border-blue-200"}`}
                    >
                      <i className="ri-app-store-line mr-1"></i>
                      {app?.label || appId}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Features */}
          {license.features && license.features.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-2`}>
                <i className="ri-check-double-line mr-1"></i> Características incluidas
              </h3>
              <ul className="space-y-1">
                {license.features.map((feature, idx) => (
                  <li key={idx} className={`flex items-center gap-2 text-sm ${themeClasses.text}`}>
                    <i className="ri-check-line text-green-500"></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Entity limits */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-2`}>
              <i className="ri-dashboard-line mr-1"></i> Límites de entidades
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {ENTITY_LIMIT_KEYS.map(({ key, label, icon }) => {
                const val = entityLimits[key as keyof typeof entityLimits];
                return (
                  <div
                    key={key}
                    className={`p-3 rounded-xl text-center ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}
                  >
                    <i className={`${icon} text-lg ${val != null ? (isDarkMode ? "text-primary-400" : "text-primary-600") : themeClasses.textSecondary}`}></i>
                    <p className={`font-bold text-sm mt-1 ${themeClasses.text}`}>
                      {val != null ? val : "∞"}
                    </p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>{label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          {license.notes && (
            <div>
              <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-1`}>
                <i className="ri-sticky-note-line mr-1"></i> Notas
              </h3>
              <p className={`text-sm ${themeClasses.text} whitespace-pre-wrap`}>{license.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex items-center justify-end gap-3 p-6 border-t ${themeClasses.border} ${themeClasses.bg}`}>
          <button
            onClick={() => onDelete(license)}
            className="px-4 py-2 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          >
            <i className="ri-delete-bin-line mr-1"></i> Desactivar
          </button>
          <button
            onClick={() => onEdit(license)}
            className="px-4 py-2.5 rounded-xl text-sm font-medium bg-primary-500 text-gray-900 hover:bg-primary-600 transition-all shadow"
          >
            <i className="ri-pencil-line mr-1"></i> Editar
          </button>
        </div>
      </div>
    </div>
  );
}
